import { CHART_TYPE_AREA, CHART_TYPE_CANDLES, SHADOW_STYLE_DEFAULT } from "../../constants";
import { toThemeColor } from "../theme";
import { createSeriesBuffers } from "./seriesBuffers";
import { highLowRange, closeRange, highLowExtremes, closeExtremes } from "./priceRange";
import { buildSeriesPalette, isRisingBar } from "./seriesPalette";
import { drawSeries } from "./drawSeries";

/**
 * The main series of the chart: Candles, Bars, Heikin Ashi or Line (Area
 * with a gradient), kept in step with the bars (buffers: seriesBuffers).
 * Colours of Candles / Bars / Heikin Ashi and the shadow style come from
 * the chart settings; the Line colours from the theme (E13).
 *
 * @param {Object<string, { css: string, rgba: number[] }>} colors - theme colours
 */
export const createMainSeries = (colors) => {
  const buffers = createSeriesBuffers(colors.lineSeries.rgba);
  let type = CHART_TYPE_CANDLES;
  let style = { up: colors.up, down: colors.down, shadowStyle: SHADOW_STYLE_DEFAULT };
  let palette = buildSeriesPalette(colors.up.rgba, colors.down.rgba);
  let bars = [];

  const isLine = () => type === CHART_TYPE_AREA;

  return {
    renderers: Object.values(buffers.renderers),

    /** @param {number} nextType - CHART_TYPE_* value; unknown values draw candles */
    setType: (nextType) => {
      if (nextType === type) return;

      type = nextType;
      buffers.build(type, bars);
    },

    /**
     * @param {{ upColor: string, downColor: string, shadowStyle: string }} nextStyle -
     *   CSS colours, SHADOW_STYLE value
     */
    setStyle: ({ upColor, downColor, shadowStyle }) => {
      style = { up: toThemeColor(upColor), down: toThemeColor(downColor), shadowStyle };
      palette = buildSeriesPalette(style.up.rgba, style.down.rgba);
    },

    /** @param {object[]} nextBars - all bars, oldest first */
    setBars: (nextBars) => {
      bars = nextBars;
      buffers.build(type, bars);
    },

    /** @param {object[]} nextBars - the same bars as in the last setBars, the last one changed */
    updateLastBar: (nextBars) => {
      bars = nextBars;
      buffers.updateLast(type, bars);
    },

    /**
     * Prices the autoscale fits: closes on Line, Heikin Ashi highs and lows
     * on Heikin Ashi, highs and lows otherwise.
     *
     * @returns {{ min: number, max: number } | null} null when the range is empty
     */
    priceRange: (first, last) =>
      isLine()
        ? closeRange(bars, first, last)
        : highLowRange(buffers.drawnBars(type, bars), first, last),

    /**
     * Highest and lowest drawn bar for the high / low labels: the same prices
     * as priceRange, so the labels stand at the drawn wicks.
     *
     * @returns {{ high: { index: number, price: number }, low: { index: number, price: number } } | null}
     */
    extremes: (first, last) =>
      isLine()
        ? closeExtremes(bars, first, last)
        : highLowExtremes(buffers.drawnBars(type, bars), first, last),

    get isLine() {
      return isLine();
    },

    /**
     * Last bar for the last price line and badge: its real close and the
     * colour of the drawn bar (the line colour on Line).
     *
     * @returns {{ index: number, price: number, color: { css: string, rgba: number[] } } | null}
     */
    get lastValue() {
      const index = bars.length - 1;

      if (index < 0) return null;

      const price = bars[index].close;

      if (isLine()) return { index, price, color: colors.lineSeries };

      const isRising = isRisingBar(buffers.drawnBars(type, bars)[index]);

      return { index, price, color: isRising ? style.up : style.down };
    },

    /**
     * @param {object} params
     * @param {number[]} params.viewport - [width, height] of the canvas, device px
     * @param {object} params.timeScale
     * @param {object} params.priceScale
     * @param {number} params.pixelRatio
     * @param {number} params.paneHeight - CSS px
     * @param {boolean} params.isSnappedX - see rectRenderer.draw
     */
    draw: (params) =>
      drawSeries({
        ...params,
        renderers: buffers.renderers,
        type,
        priceBase: buffers.priceBase,
        shadowStyle: style.shadowStyle,
        palette,
        colors,
      }),
  };
};
